import { Array as A, pipe } from 'effect';

/*
 * Invisible key markers, spec v1 (index form only).
 *
 *   U+2063 (start) + exactly 16 bits, big-endian, U+200C = 0 / U+200D = 1  →  registry index 0..65534
 *
 * A marker is always a suffix: it tags the text since the previous marker (or the start of the string). The full-key
 * form (start + 8n bits + U+2064) is reserved and decode-only; nobody emits it, it is stripped like a broken marker.
 */
export const MARKER_START = '\u2063';
const ZERO = '\u200C';
const ONE = '\u200D';
const BITS = 16;
// 65535 is reserved by the spec.
export const MAX_MARKER_INDEX = 65534;

// Any start char with its bit run and optional end: valid index markers, reserved full-key markers and broken ones.
const ANY_MARKER_RE = /\u2063[\u200C\u200D]*\u2064?/g;

export interface DecodedMarker {
  readonly index: number;
  /** Start of the text this marker tags, in the stripped string. */
  readonly start: number;
  /** End (exclusive) of the text this marker tags, in the stripped string. */
  readonly end: number;
}

export const encodeMarker = (index: number): string => {
  if (!Number.isInteger(index) || index < 0 || index > MAX_MARKER_INDEX) {
    throw new RangeError(`marker index out of range: ${index}`);
  }
  return MARKER_START + index.toString(2).padStart(BITS, '0').replace(/0/g, ZERO).replace(/1/g, ONE);
};

const bitsToIndex = (bits: string) => parseInt(bits.replace(/\u200C/g, '0').replace(/\u200D/g, '1'), 2);

const isIndexMarker = (raw: string) => raw.length === 1 + BITS && !raw.endsWith('\u2064');

export const hasMarker = (s: string): boolean => s.includes(MARKER_START);

export const stripMarkers = (s: string): string => (hasMarker(s) ? s.replace(ANY_MARKER_RE, '') : s);

/** The stripped text and every index marker in it, with the span of stripped text each one tags. */
export const decodeMarkers = (s: string): { text: string; markers: ReadonlyArray<DecodedMarker> } => {
  if (!hasMarker(s)) return { text: s, markers: [] };
  const found = Array.from(s.matchAll(ANY_MARKER_RE));
  const result = found.reduce<{ removed: number; prevEnd: number; markers: ReadonlyArray<DecodedMarker> }>(
    (acc, m) => {
      const raw = m[0];
      const at = (m.index ?? 0) - acc.removed;
      return {
        removed: acc.removed + raw.length,
        prevEnd: isIndexMarker(raw) ? at : acc.prevEnd,
        markers: isIndexMarker(raw)
          ? [...acc.markers, { index: bitsToIndex(raw.slice(1)), start: acc.prevEnd, end: at }]
          : acc.markers,
      };
    },
    { removed: 0, prevEnd: 0, markers: [] },
  );
  return { text: s.replace(ANY_MARKER_RE, ''), markers: result.markers };
};

export const markerIndexes = (s: string): ReadonlyArray<number> =>
  pipe(
    decodeMarkers(s).markers,
    A.map((m) => m.index),
  );

const isPlainObject = (v: unknown): v is Record<string, unknown> => {
  if (typeof v !== 'object' || v === null) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};

/**
 * Strips markers from strings inside plain data (strings, arrays, plain objects). Anything else — FormData, Blob,
 * class instances, Immutable collections — is returned as it is: it is not text the app built from the catalog.
 */
export const stripMarkersDeep = (value: unknown): unknown => {
  if (typeof value === 'string') return stripMarkers(value);
  if (Array.isArray(value)) return value.map(stripMarkersDeep);
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [stripMarkers(k), stripMarkersDeep(v)]));
  }
  return value;
};
